import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import { describeApiFailure } from '../../lib/failure'
import { encounterKeys } from '../encounters/queries'
import type { EncounterCard } from '../encounters/types'
import type { LiveSession, LiveSessionSummary } from './types'

export const liveKeys = {
  all: ['live'] as const,
  list: () => [...liveKeys.all, 'list'] as const,
  session: (sessionId: string) => [...liveKeys.all, 'session', sessionId] as const,
}

/**
 * Every session the caller may join.
 *
 * The cold-start read for the Live tab and the fallback for a client whose hub is down — which is
 * why it is a plain query with a refetch interval rather than something that assumes the push
 * channel works. If the hub is up this is nearly always a no-op; if it is not, the tab still works.
 */
export function useLiveSessions(
  enabled = true,
  { poll = false }: { poll?: boolean } = {},
): UseQueryResult<LiveSessionSummary[]> {
  return useQuery({
    queryKey: liveKeys.list(),
    enabled,
    queryFn: () => api.get<LiveSessionSummary[]>('/live-sessions'),
    // **Polling is off by default, and that is deliberate.** The shell holds this query on every
    // screen so it can decide whether to show the Live tab, and a poll there would be a request
    // per minute per user for something that changes a few times an evening. The hub's
    // `SessionStarted` invalidates this entry instead, which costs one request when a fight
    // actually starts and none otherwise.
    //
    // The list screen turns polling on, because that is the one place someone is *waiting* to see
    // a session appear and the one place a dead hub would otherwise look like an empty evening.
    refetchInterval: poll ? 30_000 : false,
    staleTime: 30_000,
  })
}

/**
 * One session, whole.
 *
 * **The hub writes into this same cache entry**, so a screen renders from one source whether the
 * last word came over the wire or out of a fetch. A hub that never connects degrades to a page
 * that works and does not update itself, which is the right failure.
 */
export function useLiveSession(sessionId: string, enabled = true): UseQueryResult<LiveSession> {
  return useQuery({
    queryKey: liveKeys.session(sessionId),
    enabled: enabled && sessionId !== '',
    queryFn: () => api.get<LiveSession>(`/live-sessions/${sessionId}`),
    // Never stale on its own: the hub is the update mechanism, and a background refetch racing a
    // push would be two writers to a cache entry that has exactly one.
    staleTime: Infinity,
  })
}

/**
 * Starting a session, from the encounter screen.
 *
 * Invalidates the encounter, because its `canStartLiveSession` and `liveSessionId` both change —
 * the button becomes a link, and the server is what said so.
 */
export function useStartLiveSession(encounterId: number) {
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)

  const start = useMutation({
    mutationFn: () => api.post<LiveSession>(`/encounters/${encounterId}/live`, {}),
    onSuccess: (session) => {
      setFailure(null)
      queryClient.setQueryData<LiveSession>(liveKeys.session(session.sessionId), session)
      void queryClient.invalidateQueries({ queryKey: encounterKeys.sheet(encounterId) })
      void queryClient.invalidateQueries({ queryKey: liveKeys.list() })
    },
    onError: (error: unknown) => {
      setFailure(describeApiFailure(error, 'That session could not be started.'))
    },
  })

  return { start, failure, dismissFailure: useCallback(() => setFailure(null), []) }
}

/**
 * Every write a Game Master can make during a session.
 *
 * **All of them are REST**, and none goes through the hub — that is what keeps "the GM writes,
 * everyone else reads" true by construction rather than by a flag
 * (`docs/web-api/phase-9-kickoff.md` §1, decision 2). Each answers with the whole session, so the
 * cache is written directly and nothing is re-derived.
 */
export function useSessionControls(sessionId: string) {
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)

  const path = `/live-sessions/${sessionId}`

  const write = (session: LiveSession) => {
    setFailure(null)
    queryClient.setQueryData<LiveSession>(liveKeys.session(sessionId), session)
  }

  const onError = (error: unknown) => {
    setFailure(describeApiFailure(error, 'That is not yours to change.'))
  }

  /**
   * Stands a participant on the map.
   *
   * An **instance**, not a character — six zombies share a sheet and each stands somewhere of its
   * own. There is nothing to name: the label, portrait and side are read off the participant when
   * the session is rendered.
   */
  const addToken = useMutation({
    mutationFn: (input: {
      participantId: string
      x: number
      y: number
      size?: number
      hidden?: boolean
    }) => api.post<LiveSession>(`${path}/tokens`, input),
    onSuccess: write,
    onError,
  })

  const moveToken = useMutation({
    mutationFn: (input: { tokenId: string; x: number; y: number }) =>
      api.patch<LiveSession>(`${path}/tokens/${input.tokenId}`, { x: input.x, y: input.y }),
    onSuccess: write,
    onError,
  })

  const updateToken = useMutation({
    mutationFn: (input: { tokenId: string; hidden?: boolean; size?: number; isDead?: boolean }) =>
      api.patch<LiveSession>(`${path}/tokens/${input.tokenId}`, {
        hidden: input.hidden,
        size: input.size,
        isDead: input.isDead,
      }),
    onSuccess: write,
    onError,
  })

  const removeToken = useMutation({
    mutationFn: (input: { tokenId: string }) =>
      api.delete<LiveSession>(`${path}/tokens/${input.tokenId}`),
    onSuccess: write,
    onError,
  })

  /**
   * The whole order, in order.
   *
   * Each row names a participant — never a position on a map, which is what lets a social encounter
   * with no tokens have an order at all. Rows naming somebody who has left the encounter are
   * dropped server-side rather than refused, so this list and the session cannot disagree
   * afterwards.
   */
  const setInitiative = useMutation({
    mutationFn: (input: {
      entries: { participantId: string; value: number | null; hasActed: boolean }[]
    }) => api.put<LiveSession>(`${path}/initiative`, input),
    onSuccess: write,
    onError,
  })

  /**
   * Moving between the two halves of the evening.
   *
   * Entering the encounter seeds the turn order with everyone known — the server does that, not
   * this, because "an encounter always has an order" is a rule and not a screen's opinion.
   */
  const setPhase = useMutation({
    mutationFn: (input: { phase: LiveSession['phase'] }) =>
      api.put<LiveSession>(`${path}/phase`, input),
    onSuccess: write,
    onError,
  })

  /**
   * Introducing somebody, or marking them out of the encounter.
   *
   * Both are session-only and neither reaches the character sheet: the same NPC is a stranger
   * again next week, and "dead in this encounter" is the only claim a session is entitled to make.
   */
  const updateParticipant = useMutation({
    mutationFn: (input: { participantId: string; isKnown?: boolean; isDead?: boolean }) =>
      api.patch<LiveSession>(`${path}/participants/${input.participantId}`, {
        isKnown: input.isKnown,
        isDead: input.isDead,
      }),
    onSuccess: write,
    onError,
  })

  /**
   * Another one of a `Temp` character.
   *
   * **The one control here that changes who is in the encounter**, and it is not the exception it
   * looks like: the encounter says the zombie is in this fight, and this says how many of it are
   * standing in the alley — which is an evening's decision and is kept nowhere but the session.
   * Ending the session ends the horde. Everything else about the cast is still the Encounter tab.
   */
  const addParticipant = useMutation({
    mutationFn: (input: { characterId: number; count?: number }) =>
      api.post<LiveSession>(`${path}/participants`, {
        characterId: input.characterId,
        count: input.count ?? 1,
      }),
    onSuccess: write,
    onError,
  })

  /**
   * One stress box, on one copy.
   *
   * **A session write, where every other character's stress is a sheet write.** The battle card
   * calls the ordinary character endpoint for everybody else, and it has to: a player's stress
   * track outlives the evening. A clone cannot, because six zombies share one sheet and one write
   * would tick the box on all six — so its damage is addressed by participant and is gone when the
   * session is.
   */
  const setParticipantStress = useMutation({
    mutationFn: (input: { participantId: string; slotId: number; isStressed: boolean }) =>
      api.patch<LiveSession>(`${path}/participants/${input.participantId}/stress`, {
        slotId: input.slotId,
        isStressed: input.isStressed,
      }),
    onSuccess: write,
    onError,
  })

  /**
   * One copy gone, with its token and its turn.
   *
   * The last one cannot go this way — the encounter still says that character is in the fight, so
   * the next sync would put it straight back. Taking somebody out entirely is an encounter edit.
   */
  const removeParticipant = useMutation({
    mutationFn: (input: { participantId: string }) =>
      api.delete<LiveSession>(`${path}/participants/${input.participantId}`),
    onSuccess: write,
    onError,
  })

  const advance = useMutation({
    mutationFn: () => api.post<LiveSession>(`${path}/initiative/advance`, {}),
    onSuccess: write,
    onError,
  })

  const end = useMutation({
    mutationFn: () => api.delete<void>(path),
    onSuccess: () => {
      setFailure(null)
      void queryClient.invalidateQueries({ queryKey: liveKeys.session(sessionId) })
      void queryClient.invalidateQueries({ queryKey: liveKeys.list() })
    },
    onError,
  })

  return {
    addToken,
    moveToken,
    updateToken,
    removeToken,
    setInitiative,
    setPhase,
    updateParticipant,
    addParticipant,
    removeParticipant,
    setParticipantStress,
    advance,
    end,
    failure,
    dismissFailure: useCallback(() => setFailure(null), []),
  }
}

export type SessionControls = ReturnType<typeof useSessionControls>

/**
 * One participant's combat card.
 *
 * The single-card read is **not** Game Master-only, unlike the whole-encounter one: a caller who
 * owns the character may read it, because they already own that sheet and learn nothing new. That
 * is what lets a player watching a fight see their own numbers without opening a second tab
 * (`EncountersController.Card` records the reasoning).
 */
export function useEncounterCard(
  encounterId: number,
  characterId: number | null,
  enabled = true,
): UseQueryResult<EncounterCard> {
  return useQuery({
    queryKey: [...encounterKeys.cards(encounterId), 'one', characterId],
    enabled: enabled && characterId !== null && Number.isFinite(encounterId),
    queryFn: () => api.get<EncounterCard>(`/encounters/${encounterId}/cards/${characterId}`),
  })
}
